// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Button,
  Card,
  Description,
  H2,
  LgPadding,
  LoadingEmptyState,
  MdSpacing,
  SecondaryPage,
  XlPadding,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { InfoRequestHeader } from './InfoRequestHeader';
import { type HubReferralRouteParams } from './LandingPage';
import { NavBarLogo } from './NavBarLogo';
import { PatientAuthorizationActionSheet } from './PatientAuthorizationActionSheet';
import { useQueryPatientAuthPageContent } from '~web/features/info-request/hooks';
import { useStatusBarColor } from '~web/features/info-request/hooks/useStatusBarColor';

export const PatientAuthorizationPage = () => {
  const { goBack, navigate } = useNavigation();
  const { hubReferralID: hubReferralIDParam } = useParams<HubReferralRouteParams>();
  const hubReferralID = Number(hubReferralIDParam || 0);
  const { trackPageView } = useAnalytics();
  const { setActiveActionSheet, closeActionSheet } = useContext(ActionSheetContext);
  const { isFetching, headerLogoUrl, pageHeader, pageBody, authContentTitle, authContentBody, nextURL, redirectURL } =
    useQueryPatientAuthPageContent({
      hubReferralID,
    });

  const handlePress = () => {
    setActiveActionSheet(
      <PatientAuthorizationActionSheet
        hubReferralID={hubReferralID}
        onClose={closeActionSheet}
        nextURL={nextURL}
      />,
    );
  };

  useStatusBarColor(COLORS.BACKGROUND_COLORS.GREY_LIGHTEST);

  useEffect(() => {
    trackPageView({
      event: EVENTS.INFO_REQUEST__PATIENT_AUTHORIZATION_PAGE_VIEWED,
      params: { hubReferralID },
    });
  }, [trackPageView, hubReferralID]);

  useEffect(() => {
    if (redirectURL) {
      navigate(redirectURL);
    }
  }, [navigate, redirectURL]);

  if (isFetching) {
    return (
      <XlPadding>
        <LoadingEmptyState />
      </XlPadding>
    );
  }

  if (!pageHeader || !authContentTitle || !authContentBody) {
    return null;
  }

  return (
    <SecondaryPage
      footerPlacementContext="webScreenWithoutPolicyFooter"
      withoutWebFramingElementHeights
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.GREY_LIGHTEST}
      dismissIcon="chevronleft"
      onDismiss={goBack}
      NavBarCenterContent={headerLogoUrl ? <NavBarLogo illustrationSrc={headerLogoUrl} /> : undefined}
      HeaderContent={
        <InfoRequestHeader
          title={pageHeader}
          subtitle={pageBody}
        />
      }
      buttons={[
        <Button
          label="Sign form"
          key="sign form"
          onPress={handlePress}
        />,
      ]}
    >
      <Card>
        <LgPadding>
          <H2>{authContentTitle}</H2>
          <MdSpacing />
          <Description>{authContentBody}</Description>
        </LgPadding>
      </Card>
    </SecondaryPage>
  );
};
